
// region JS Imports

// region External Modules
import _template from "lodash/template"
// endregion External Modules

// region Private Modules
import DataRetriever from "app/data_retriever"
import NumbersFormatter from "app/numbers/formatter"

import dayjs from "plugins/dayjs/index"
import I18n from "plugins/i18n-js"

import Highcharts from "plugins/highcharts"
// endregion Private Modules

// region Public Components
import EconomicIndicatorHighchartDisplay from "./../../../../../../shared/economic_indicator_highchart_display/index"
// endregion Public Components

// region Private Components
import tooltip_default from "./tooltip_default.html._tpl"
import tooltip_for_period_comparison from "./tooltip_for_period_comparison.html._tpl"
import tooltip_for_economic_indicator_comparison from "./tooltip_for_economic_indicator_comparison.html._tpl"
// endregion Private Components

// endregion JS Imports


const tooltip_default_tmpl = _template(tooltip_default)
const tooltip_for_period_comparison_tmpl = _template(tooltip_for_period_comparison)
const tooltip_for_economic_indicator_comparison_tmpl = _template(tooltip_for_economic_indicator_comparison)


// region Helper functions

function main_data_zones_collection(first_index: number, last_index: number) {
  if ( first_index < 0 ) {
    return [
      {
        color: "transparent",
      },
    ]
  }
  else {
    return [
      {
        value: first_index,
        color: "transparent",
      },
      {
        value: last_index + 0.00000001,
        color: "#1D3960",
      },
      {
        color: "transparent",
      },
    ]
  }
}

function main_series(group_mean_price_per_net_area_unit: any) {
  const first_index = group_mean_price_per_net_area_unit.findIndex((element) => element.mean_price_per_net_area_unit > 0)
  const last_index = group_mean_price_per_net_area_unit.findLastIndex((element) => element.mean_price_per_net_area_unit > 0)

  return {
    type:     "line",
    color:    "#1D3960",
    symbol:   "&#9644",
    data:     group_mean_price_per_net_area_unit.map((e, index) => {
      let value_to_be_used = e.mean_price_per_net_area_unit
      let enabled = true

      if ( value_to_be_used == null && first_index >= 0 ) {
        enabled = false

        if ( index < first_index ) {
          value_to_be_used = group_mean_price_per_net_area_unit[first_index].mean_price_per_net_area_unit
        }
        else if ( index > last_index ) {
          value_to_be_used = group_mean_price_per_net_area_unit[last_index].mean_price_per_net_area_unit
        }
        else {
          let previous_value = 0
          let previous_index = 0
          let future_value = 0
          let future_index = 0

          for( let current_index = index; current_index >= 0; current_index--) {
            if ( group_mean_price_per_net_area_unit[current_index].mean_price_per_net_area_unit != null ) {
              previous_index = current_index
              previous_value = group_mean_price_per_net_area_unit[current_index].mean_price_per_net_area_unit

              break
            }
          }

          for( let current_index = index; current_index <= group_mean_price_per_net_area_unit.length; current_index++) {
            if ( group_mean_price_per_net_area_unit[current_index].mean_price_per_net_area_unit != null ) {
              future_index = current_index
              future_value = group_mean_price_per_net_area_unit[current_index].mean_price_per_net_area_unit

              break
            }
          }

          const value_offset = (future_value - previous_value) * (future_index - index) / (future_index - previous_index)

          value_to_be_used = future_value - value_offset
        }
      }

      return {
        y:       first_index < 0 ? 15000 : value_to_be_used,
        marker:  {
          enabled:  enabled,
          states:   {
            hover: {
              enabled: enabled,
            },
          },
        },
      }
    }),
    zoneAxis: "x",
    zones:    main_data_zones_collection(first_index, last_index),
  }
}

function compared_series(group_mean_price_per_net_area_unit_for_period_comparison: any, filtered_spacious_economic_indicator_data_entries: any) {
  if ( group_mean_price_per_net_area_unit_for_period_comparison.length > 0 ) {
    return {
      type:       "line",
      color:      "#8B61E4",
      dashStyle:  "ShortDash",
      symbol:     "&#9477",
      data:       group_mean_price_per_net_area_unit_for_period_comparison.map((e) => {
        return {
          y:       e.mean_price_per_net_area_unit,
          marker:  {
            enabled:  e.mean_price_per_net_area_unit != null,
            states:   {
              hover: {
                enabled: e.mean_price_per_net_area_unit != null,
              },
            },
          },
        }
      }),
    }
  }
  else if ( filtered_spacious_economic_indicator_data_entries.length > 0 ) {
    return {
      type:       "line",
      yAxis:      1,
      color:      "#8B61E4",
      dashStyle:  "ShortDash",
      symbol:     "&#9477",
      data:       filtered_spacious_economic_indicator_data_entries.map((e) => {
        return {
          y:        e.numerical_value,
          marker:  {
            enabled:  e.numerical_value != null,
            states:   {
              hover: {
                enabled: e.numerical_value != null,
              },
            },
          },
        }
      }),
    }
  }
  else {
    return {}
  }
}

// endregion Helper functions


const create = ($line_graph_container: JQuery<HTMLElement>, data: any, compare_option: string | null, offset_right: number | null): any => {
  const currency_unit_for_display = data.currency_unit_for_display || DataRetriever.get("currency-unit")
  const group_mean_price_per_net_area_unit = data.group_mean_price_per_net_area_unit
  const chart_date_label_format_type = data.chart_date_label_format_type

  const group_mean_price_per_net_area_unit_for_period_comparison = data.group_mean_price_per_net_area_unit_for_period_comparison

  const filtered_spacious_economic_indicator_data_entries = data.filtered_spacious_economic_indicator_data_entries
  const spacious_economic_indicator_date_label_format_type = data.spacious_economic_indicator_date_label_format_type

  // this function only need to be disabled here
  Highcharts.Pointer.prototype.reset = function() {
    return null
  }

  // @ts-ignore
  return Highcharts.chart($line_graph_container[0] as Highcharts.HTMLDOMElement, {
    chart:    {
      marginLeft:   80,
      marginRight:  offset_right,
      spacingLeft:  0,
      events:      {
        load: function() {
          const chart = this
          const firstSeries = chart.series[0]
          const lastSeries = chart.series[1]

          if ( firstSeries && lastSeries && firstSeries.points.length < lastSeries.points.length ) {
            for (let i = firstSeries.points.length; i < lastSeries.points.length; i++) {
              firstSeries.addPoint(
                {
                  y: null,
                },
                false,
              )
            }
            chart.redraw()
          }
        },
      },
    },
    title:    {
      text: "",
    },
    subtitle: {
      text: "",
    },
    credits:  {
      enabled: false,
    },
    legend:   {
      enabled: false,
    },

    xAxis:       {
      crosshair: {
        width:      1,
        color:      "gray",
        dashStyle:  "shortdot",
        zIndex:     1000000,
      },
      categories: group_mean_price_per_net_area_unit.map(({first_day_of_period}) => {
        const prefix = chart_date_label_format_type === "Q YYYY" ? "Q" : ""

        return [
          prefix,
          dayjs(first_day_of_period).add(0, "month").locale(I18n.locale.toLowerCase()).format(chart_date_label_format_type),
        ].join("")
      }),
      lineColor:    "#F7F7F7",
      labels:       {
        rotation: 0,
        enabled:  true,
        useHTML:  true,
        align:    "center",
        overflow: "visible",
        style:    {
          fontSize:   "12px",
          fontColor:  "#1d3960",
        },
      },
      tooltip:      {
        enabled: false,
      },
      tickAmount:   12,
      tickInterval: Math.ceil(group_mean_price_per_net_area_unit.length / 12),
    },
    yAxis:       [
      {
        title:     {
          text:  "",
        },
        min:       0,
        // https://api.highcharts.com/highcharts/yAxis.max
        // Max value would be automatically calculated if set it as null.
        // Set it as 30000 if no data available
        max:       Math.max(...group_mean_price_per_net_area_unit.map((e) => e.mean_price_per_net_area_unit)) <= 0 ? 30000 : null,
        lineWidth: 2,
        lineColor: "transparent",
        labels:    {
          // https://api.highcharts.com/highcharts/xAxis.labels.align
          align:        "left",
          reserveSpace: true,
          style:        {
            fontSize:  "13px",
            fontColor: "#1d3960",
          },
          formatter:    function() {
            const price_label = NumbersFormatter.numberAlwaysWithCommasAndSuffix(this.value as number) || "0"

            return `${currency_unit_for_display}${price_label}`
          },
        },
        // limit max number of yAxis to avoid unexpected overflow
        tickAmount:    6,
        gridLineColor: "#F7F7F7",
      },
      {
        opposite:       true,
        title:          {
          text:  "",
        },
        min:            Math.min(Math.min(...filtered_spacious_economic_indicator_data_entries.map((e) => e.numerical_value)), 0),
        labels:         {
          style:  {
            color:    "#c4c4c4",
            fontSize: "13px",
          },
          align:        "right",
          reserveSpace: true,
          formatter:    function() {
            return  EconomicIndicatorHighchartDisplay.yAxisNumberFormatter(compare_option, this.value as number)
          },
        },
        // limit max number of yAxis to avoid unexpected overflow
        tickAmount:     6,
        gridLineColor:  "#F7F7F7",
        allowDecimals:  EconomicIndicatorHighchartDisplay.should_allow_decimals(compare_option),
      },
    ],
    plotOptions: {
      line: {
        stickyTracking: false,
      },
      series: {
        connectNulls: true,
        marker:       {
          radius:  3,
          symbol:  "circle",
          states:  {
            hover: {
              radius: 6,
            },
          },
        },
        states:  {
          hover: {
            lineWidth: 2,
          },
          inactive: {
            enabled: false,
          },
        },
      },
    },
    tooltip:     {
      shared:     true,
      positioner: function(boxWidth, boxHeight, point) {
        // modify from example: https://jsfiddle.net/jugal/bAw5J/
        const chart = this.chart
        const distance = 16

        let x = chart.plotLeft + point.plotX + distance

        if ( (x + boxWidth) > (chart.plotLeft + chart.plotWidth) ) {
          x = point.plotX + chart.plotLeft - boxWidth - distance
        }

        let y
        if (point.y) {
          y = point.plotY - boxHeight / 2.0
        } else {
          y = (chart.plotHeight - boxHeight) / 2.0
        }

        return {
          x: x,
          y: y,
        }
      },
      useHTML:          true,
      backgroundColor:  null,
      borderWidth:      0,
      shadow:           false,
      style:            {
        padding: 0,
      },
      formatter: function() {
        const current_index = this.point.index

        const main_series_symbol_color = "#1D3960"
        const main_series_symbol = "&#9644"
        const main_series_data = group_mean_price_per_net_area_unit[current_index]
        const main_date_label = [
          chart_date_label_format_type === "Q YYYY" ? "Q" : "",
          dayjs(main_series_data.first_day_of_period).add(0, "month").locale(I18n.locale.toLowerCase()).format(chart_date_label_format_type),
        ].join("")
        const mean_price_per_net_area_unit = main_series_data.mean_price_per_net_area_unit
        const formatted_main_price_value = NumbersFormatter.formattedNumberLabelForDisplay({
          value:            mean_price_per_net_area_unit,
          prefix:           currency_unit_for_display,
          use_exact_value:  true,
        }) || "-"

        if ( compare_option === "none" ) {
          return tooltip_default_tmpl({
            main_symbol_color:  main_series_symbol_color,
            main_symbol:        main_series_symbol,
            main_date_label:    main_date_label,
            main_value:         formatted_main_price_value,
          })
        }
        else if ( group_mean_price_per_net_area_unit_for_period_comparison.length > 0 ) {
          const current_value = group_mean_price_per_net_area_unit[this.point.index]?.mean_price_per_net_area_unit
          const prev_value = group_mean_price_per_net_area_unit_for_period_comparison[this.point.index]?.mean_price_per_net_area_unit
          const percentage_diff = (current_value - prev_value) / prev_value
          const percentage_text = Number(percentage_diff).toLocaleString(I18n.locale,{style: "percent",minimumFractionDigits: 1, maximumFractionDigits: 1})

          let percentage_label = ""
          let extra_class = ""

          if ( current_value && prev_value ) {
            if (percentage_diff >= 0) {
              percentage_label = `+${percentage_text}`
              extra_class = "positive-growth"
            }
            else {
              percentage_label = `${percentage_text}`
              extra_class = "negative-growth"
            }
          }

          const compared_series_symbol_color = "#8B61E4"
          const compared_series_symbol = "&#9477"
          const compared_series_data = group_mean_price_per_net_area_unit_for_period_comparison[current_index]
          const compared_date_string = [
            chart_date_label_format_type === "Q YYYY" ? "Q" : "",
            dayjs(compared_series_data.first_day_of_period).add(0, "month").locale(I18n.locale.toLowerCase()).format(chart_date_label_format_type),
          ].join("")
          const formatted_compared_price_value = NumbersFormatter.formattedNumberLabelForDisplay({
            value:            compared_series_data.mean_price_per_net_area_unit,
            prefix:           currency_unit_for_display,
            use_exact_value:  true,
          }) || "-"

          return tooltip_for_period_comparison_tmpl({
            percentage_label:     percentage_label,
            extra_class:          extra_class,

            main_symbol_color:  main_series_symbol_color,
            main_symbol:        main_series_symbol,
            main_date_label:    main_date_label,
            main_value:         formatted_main_price_value,

            compared_symbol_color:  compared_series_symbol_color,
            compared_symbol:        compared_series_symbol,
            compared_date_label:    compared_date_string,
            compared_value:         formatted_compared_price_value,
          })
        }
        else if ( filtered_spacious_economic_indicator_data_entries.length > 0 ) {
          const compared_series_symbol_color = "#8B61E4"
          const compared_series_symbol = "&#9477"
          const compared_series_data = filtered_spacious_economic_indicator_data_entries[current_index]
          const compared_numerical_value = compared_series_data.numerical_value
          const compared_date_value = compared_series_data.date_value

          return tooltip_for_economic_indicator_comparison_tmpl({
            main_symbol_color:  main_series_symbol_color,
            main_symbol:        main_series_symbol,
            main_date_label:    main_date_label,
            main_value:         formatted_main_price_value,

            economic_indicator_title: I18n.t(`apps_spacious_website_pages.building_level_real_estate_transaction_list_page.sections.compare_section.economic_indicator_comparison.options.default.${compare_option}`),
            compared_symbol_color:    compared_series_symbol_color,
            compared_symbol:          compared_series_symbol,
            compared_date_label:      EconomicIndicatorHighchartDisplay.tooltipMonthFormatter(compare_option, compared_date_value, spacious_economic_indicator_date_label_format_type),
            compared_value:           EconomicIndicatorHighchartDisplay.tooltipNumberFormatter(compare_option, compared_numerical_value),
          })
        }
      },
    },

    series: [
      main_series(group_mean_price_per_net_area_unit),
      compared_series(group_mean_price_per_net_area_unit_for_period_comparison, filtered_spacious_economic_indicator_data_entries),
    ],
  })

}

export default {create}
